import   React                   ,
       { useEffect               ,
	     useMemo                 ,
	     useState                }  from "react";

import   classNames                 from 'classnames';

import   styles                     from './AdminList.module.scss';

import   Field                      from '../../widget/inputs/Field';
import   Select                     from '../../widget/inputs/Select';
import   Toggle                     from '../../widget/inputs/Toggle';
import   CountrySelector            from '../../widget/inputs/CountrySelector';
import   AnimatedWaitingIcon2       from '../../widget/animations/animatedWaitingIcon2';
import   ColumnTitle                from '../../widget/dataDisplay/labels/ColumnTitle'
import   Window                     from '../../widget/windows/window';
import   AdminRow                   from './AdminRow';
import   AdminSheet                 from './AdminSheet';

import { Admin                   ,
         ACCOUNT_STATUS_VALUES    ,
         ACCOUNT_STATUS_LABELS    }  from '../../../dataTypes/adminManagment/types';
import { getAdmins               ,
         deleteAdmin              }  from '../../../backendServices/adminManagment/api';

import { USER_DELETION_CONFIRMATION_REQUIRED_CODE ,
         ALREADY_PSEUDO_DELETED_CODE               }  from '../../../dataTypes/backendServicesManagment/types';

import { useTypedSelector        }  from '../../../store/useTypeSelector';
import { RootState               }  from '../../../store/generalReducer';

export const AdminList = () => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  const [ adminSet2            , setAdminSet2            ] = useState<Admin[]>([]);

  const [ showWaitingIcon      , setShowWaitingIcon      ] = useState(false);

  const [ filter                , setFilter                ] = useState('');
  // Bascule comptes actifs / comptes supprimés (pseudo-suppression, statut 'deleted').
  // Par défaut : non supprimés. En mode supprimés, statut et vérification n'ont plus de sens.
  const [ showDeleted           , setShowDeleted           ] = useState(false);
  const [ statusFilter          , setStatusFilter          ] = useState('');
  const [ verifiedFilter        , setVerifiedFilter        ] = useState('');
  const [ countryFilter         , setCountryFilter         ] = useState('');
  const SORT_CHOICES = [ '' , 'lastname_asc' , 'lastname_desc' , 'pseudonym_asc' , 'pseudonym_desc' ,
                         'creationDate_desc' , 'creationDate_asc' , 'lastLogin_desc' , 'lastLogin_asc' ];
  const [ sortOption            , setSortOption            ] = useState('');

  const [ viewAdminIndex        , setViewAdminIndex        ] = useState<number | null>(null);
  const [ createNewAdmin        , setCreateNewAdmin        ] = useState<boolean>(false);

  const [ showMessageWindow     , setShowMessageWindow     ] = useState(false);
  const [ message               , setMessage               ] = useState(<></>);

  const [ showConfirmation      , setShowConfirmation      ] = useState(false);
  const [ adminToDelete         , setAdminToDelete         ] = useState<Admin | null>(null);

  // Second palier : le compte à supprimer est lié à un User existant (email affiché
  // dans associatedUserEmail) — il faut trancher entre le supprimer aussi ou ne faire
  // qu'une rétrogradation, avant de rappeler deleteAdmin avec ce choix explicite.
  const [ showUserDeletionChoice , setShowUserDeletionChoice ] = useState(false);
  const [ associatedUserEmail    , setAssociatedUserEmail    ] = useState('');

  useEffect(() => {

    const fetchData = async () => {

      try
       {
        setShowWaitingIcon(true);

        const adminsResult = await getAdmins();

        if (adminsResult.payload)
         {
          setAdminSet2 (adminsResult.payload);
         }
       }
      catch (error)
       {
        console.error('Error fetching data:', error);
       }
      finally
       {
        setShowWaitingIcon(false);
       }
    };

    fetchData();
  }, []);

  const reinit = async () => {

    setShowWaitingIcon(true);

    const adminsResult = await getAdmins();

    if (adminsResult.payload)
     {
      setAdminSet2 (adminsResult.payload);
     }

    setShowWaitingIcon(false);
  }

  const checkFilter = (subChaine: string, chaine: string) => {
    if (subChaine === '') return true;
    return chaine.toUpperCase().includes(subChaine.toUpperCase());
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };

  const handleVerifiedFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setVerifiedFilter(e.target.value);
  };

  const handleSortOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value);
  };

  // Clic sur un titre de colonne : même colonne, sens inverse ; autre colonne, croissant.
  const [ sortField , sortDirection ] = sortOption.split('_') as [ string , 'asc' | 'desc' ];
  const handleColumnSort = (key: string) => {
    setSortOption(`${key}_${sortField === key && sortDirection === 'asc' ? 'desc' : 'asc'}`);
  };

  // Liste affichée : dataset brut (adminSet2) + recherche texte + filtres + tri
  const adminSet = useMemo(() => {

    const filtered = adminSet2.filter((admin) =>
      ( showDeleted ? admin.accountStatus === 'deleted' : admin.accountStatus !== 'deleted' ) &&
      (
       filter === ''                            ||
       checkFilter(filter, admin.lastname)      ||
       checkFilter(filter, admin.firstname)     ||
       checkFilter(filter, admin.pseudonym)     ||
       checkFilter(filter, admin.city)          ||
       checkFilter(filter, admin.email)
      )                                                                        &&
      ( showDeleted || statusFilter   === '' || admin.accountStatus === statusFilter )          &&
      ( showDeleted || verifiedFilter === '' || (verifiedFilter === 'yes' ? admin.verified : !admin.verified) ) &&
      (countryFilter  === '' || admin.country === countryFilter)
    );

    if (sortOption === '') return filtered;

    const [ sortField , sortDirection ] = sortOption.split('_');

    const sorted = [ ...filtered ].sort((a, b) => {

      let comparison = 0;

      if (sortField === 'lastname')     comparison = a.lastname.localeCompare(b.lastname, 'fr');
      if (sortField === 'verified')     comparison = Number(!!a.verified) - Number(!!b.verified);
      if (sortField === 'subscribers')  comparison = (a.nbSubscribers ?? 0) - (b.nbSubscribers ?? 0);
      if (sortField === 'hours')        comparison = (a.broadcastSeconds ?? 0) - (b.broadcastSeconds ?? 0);
      if (sortField === 'sessions')     comparison = (a.broadcastSessions ?? 0) - (b.broadcastSessions ?? 0);
      if (sortField === 'storage')      comparison = Number(a.quotaUsed ?? 0) - Number(b.quotaUsed ?? 0);
      if (sortField === 'location')     comparison = (a.city ?? '').localeCompare(b.city ?? '', 'fr');
      if (sortField === 'status')       comparison = (a.accountStatus ?? '').localeCompare(b.accountStatus ?? '', 'fr');
      if (sortField === 'pseudonym')    comparison = a.pseudonym.localeCompare(b.pseudonym, 'fr');
      if (sortField === 'lastLogin')    comparison = (a.lastLoginDate?.getTime() ?? 0) - (b.lastLoginDate?.getTime() ?? 0);
      if (sortField === 'creationDate') comparison = (a.creationDate?.getTime() ?? 0)  - (b.creationDate?.getTime() ?? 0);

      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return sorted;

  }, [ adminSet2 , filter , showDeleted , statusFilter , verifiedFilter , countryFilter , sortOption ]);

  const handleEditionClick = (index: number) => {
    setViewAdminIndex(index);
  };

  const handleDeleteClick = (admin: Admin) => {
    setAdminToDelete(admin);
    setShowConfirmation(true);
  };

  const handleDeleteConfirmation = async (confirmed: boolean) => {

    if ( confirmed && adminToDelete )
     {
      try
       {
        // Premier appel sans trancher : si un User est lié, AdminDeleting répond
        // 409/USER_DELETION_CONFIRMATION_REQUIRED_CODE plutôt que de supprimer quoi que
        // ce soit — la question se pose avant, jamais après coup.
        const response = await deleteAdmin ( adminToDelete.id , authentication.admin?.id );

        if (response.code === USER_DELETION_CONFIRMATION_REQUIRED_CODE)
              {
               setAssociatedUserEmail (response.comment);
               setShowUserDeletionChoice (true);
               setShowConfirmation (false);
               return;
              }

        if (response.code === 1)
              {
               setAdminSet2 ((prev) => prev.filter((admin) => admin.id !== adminToDelete.id));
               setMessage(<></>);
               setShowMessageWindow(false);
              }
         else {
               setMessage (
                           response.code === -103
                           ? <>Impossible de supprimer ce compte car il est lié à d'autres ressources</>
                           : response.code === ALREADY_PSEUDO_DELETED_CODE
                           ? <>{response.comment}</>
                           : <>Impossible de supprimer ce compte</>
                          );
               setShowMessageWindow(true);
              }
       }
      catch (error)
       {
        console.error(`Erreur lors de la suppression de l'admin ID ${adminToDelete.id}:`, error);
        setMessage(<>Impossible de supprimer ce compte</>);
        setShowMessageWindow(true);
       }
     }

    setShowConfirmation(false);
    setAdminToDelete(null);
  };

  // Second palier : deleteUser tranche entre supprimer aussi le User associé (son email
  // est alors libéré) ou ne faire qu'une rétrogradation (le User est conservé tel quel).
  const handleUserDeletionChoice = async (deleteUser: boolean) => {

    setShowUserDeletionChoice (false);
    setAssociatedUserEmail ('');

    if ( !adminToDelete ) { return; }

    try
     {
      const response = await deleteAdmin ( adminToDelete.id , authentication.admin?.id , deleteUser );

      if (response.code === 1)
            {
             setAdminSet2 ((prev) => prev.filter((admin) => admin.id !== adminToDelete.id));
             setMessage(<></>);
             setShowMessageWindow(false);
            }
       else {
             setMessage (
                         response.code === -103
                         ? <>Impossible de supprimer ce compte car il est lié à d'autres ressources</>
                         : response.code === ALREADY_PSEUDO_DELETED_CODE
                         ? <>{response.comment}</>
                         : <>Impossible de supprimer ce compte</>
                        );
             setShowMessageWindow(true);
            }
     }
    catch (error)
     {
      console.error(`Erreur lors de la suppression de l'admin ID ${adminToDelete.id}:`, error);
      setMessage(<>Impossible de supprimer ce compte</>);
      setShowMessageWindow(true);
     }

    setAdminToDelete(null);
  };

  const handleMessageWindowClose = () => {
    setShowMessageWindow(false);
  };

  const handleNewAdminCreationClick = () => {
    setCreateNewAdmin(true)
  };

  const handleAdminCreate = ( id: number ) => {

    setCreateNewAdmin (false)
    reinit ()
  }

  const handleAdminSheetClose = ( id: number ) => {

    setViewAdminIndex (null)
    reinit ()
  }

  const renderComponent = () : JSX.Element => {

           if ( createNewAdmin )
                 {
                  return <AdminSheet onCreate = {handleAdminCreate} onEdit = {handleAdminCreate} />
                 }
            else {
                  if ( viewAdminIndex !== null )
                        {
                         return <AdminSheet admin  = {adminSet[viewAdminIndex]}
                                             onEdit = {handleAdminSheetClose} />
                        }
                   else {
                         return (
                                  <div className={classNames(styles.frame)}>

                                    <div className={classNames(styles.head)}>
                                      <div className={classNames(styles.headTitleBlock)}>
                                        <span className={classNames(styles.headTitle)}>Liste des créateurs</span>
                                        <span className={classNames(styles.headSubtitle)}>
                                          Consultez et gérez tous les comptes créateurs
                                        </span>
                                      </div>
                                      <div className={classNames(styles.headActions)}>
                                        <button className={classNames(styles.btn, styles.btnPrimary)}
                                                onClick={handleNewAdminCreationClick}>
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <line x1="12" y1="5" x2="12" y2="19"/>
                                            <line x1="5" y1="12" x2="19" y2="12"/>
                                          </svg>
                                          Créer un compte
                                        </button>
                                      </div>
                                    </div>

                                    <div className={classNames(styles.upper_bar)}>

                                      <Field type        = "text"
                                             placeholder = "Rechercher..."
                                             value       = {filter}
                                             width       = "280px"
                                             onChange    = {handleFilterChange} />

                                      <Toggle form             = "switch"
                                              checked          = {showDeleted}
                                              onChange         = {setShowDeleted}
                                              show             = "text"
                                              internalPosition = {false}
                                              textOff          = "En cours"
                                              textOn           = "Archivés"
                                              textColorOn      = "var(--color-Panel-niv1-FG)"
                                              textColorOff     = "var(--color-Panel-niv1-FG)"
                                              fillColorOn      = "#C30000"
                                              fillColorOff     = "#6B7280"
                                              persistentIcons  = {true}
                                              width            = "44px"
                                              height           = "30px" />

                                      {
                                       !showDeleted &&
                                       (
                                        <>
                                          <Select choices          = {[ '' , ...ACCOUNT_STATUS_VALUES ]}
                                                  choicesLabel     = {[ 'Tous les statuts' , ...ACCOUNT_STATUS_LABELS ]}
                                                  value            = {statusFilter}
                                                  showPlaceholder  = {false}
                                                  width            = "170px"
                                                  onChange         = {handleStatusFilterChange} />

                                          <Select choices          = {[ '' , 'yes' , 'no' ]}
                                                  choicesLabel     = {[ 'Tous (vérifié)' , 'Vérifié' , 'Non vérifié' ]}
                                                  value            = {verifiedFilter}
                                                  showPlaceholder  = {false}
                                                  width            = "170px"
                                                  onChange         = {handleVerifiedFilterChange} />
                                        </>
                                       )
                                      }

                                      <CountrySelector value      = {countryFilter}
                                                       allOption  = {{ value: '', label: 'Tous les pays' }}
                                                       width      = "190px"
                                                       onChange   = {setCountryFilter} />

                                      <Select choices          = {SORT_CHOICES}
                                              choicesLabel     = {[ 'Trier par…'
                                                                   , 'Nom (A → Z)'                     , 'Nom (Z → A)'
                                                                   , 'Pseudo (A → Z)'                  , 'Pseudo (Z → A)'
                                                                   , 'Date de création (récent)'       , 'Date de création (ancien)'
                                                                   , 'Dernière connexion (récent)'     , 'Dernière connexion (ancien)' ]}
                                              // Tri choisi par une colonne absente de cette liste : retour à « Trier par… ».
                                              value            = {SORT_CHOICES.includes(sortOption) ? sortOption : ''}
                                              showPlaceholder  = {false}
                                              width            = "230px"
                                              onChange         = {handleSortOptionChange} />

                                    </div>

                                    <div className={classNames(styles.list_container)}>

                                      <div className={classNames(styles.list)}>
                                        <ColumnTitle minWidth      = "2020px"
                                                     sortKeys      = {[ 'lastname' , 'pseudonym' , 'verified' , 'subscribers' , 'hours' , 'sessions' , 'storage' , 'location' , 'status' , 'creationDate' , 'lastLogin' , null ]}
                                                     sortKey       = {sortField}
                                                     sortDirection = {sortDirection}
                                                     onSort        = {handleColumnSort}
                                                     titles = {[ 'Créateur' , 'Nom public' , 'Vérifié' , 'Abonnés' , 'Heures' , 'Sessions' , 'Stockage' , 'Localisation' , 'Statut' , 'Date de création' , 'Dernière connexion' , 'Action' ]}
                                                     widths = {[ '340px'    , 'auto'      , '90px'   , '110px'   , '90px'   , '90px'     , '170px'    , '160px'         , '160px'  , '190px'            , '190px'              , '110px'  ]} />

                                        {
                                         adminSet.map ( (admin, index) => (
                                                                            <AdminRow key      = {admin.id}
                                                                                      admin    = {admin}
                                                                                      index    = {index}
                                                                                      onEdit   = {handleEditionClick}
                                                                                      onDelete = {handleDeleteClick} />
                                                                          )
                                                        )
                                        }
                                      </div>

                                    </div>

                                  </div>
                                )
                        }
                 }
  }

  return (
    <div className={classNames(styles.root)}>

      {
        showWaitingIcon && <AnimatedWaitingIcon2 />
      }

      {
        showMessageWindow &&
        <Window message                 = {message}
                messageType             = "information"
                onClickOnValidateButton = {handleMessageWindowClose}
                validButtonText         = "Ok" />
      }

      {
        showConfirmation &&
        <Window
          message                 = {<>{`Êtes-vous sûr de vouloir supprimer le compte de ${adminToDelete?.firstname} ${adminToDelete?.lastname} ?`}</>}
          messageType             = "question"
          onClickOnValidateButton = {() => handleDeleteConfirmation(true)}
          onClickOnCancelButton   = {() => handleDeleteConfirmation(false)}
          validButtonText         = "Oui"
          cancelButtonText        = "Non" />
      }

      {
        showUserDeletionChoice &&
        <Window
          message                 = {<>Ce compte créateur est lié à un compte utilisateur existant ({associatedUserEmail}). Faut-il aussi supprimer ce compte utilisateur (son email sera alors libéré), ou seulement retirer le statut créateur en conservant le compte client ?</>}
          messageType             = "question"
          onClickOnValidateButton = {() => handleUserDeletionChoice(true)}
          onClickOnCancelButton   = {() => handleUserDeletionChoice(false)}
          validButtonText         = "Supprimer aussi le compte utilisateur"
          cancelButtonText        = "Non, juste rétrograder"
          width                   = "900px" />
      }

      {renderComponent ()}
    </div>
  );
};

export default AdminList;
